import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-hot-toast';

/**
 * useAgentDraftCart Hook
 * 
 * Manages draft carts for field agents with auto-save functionality
 * Each company gets its own cart that persists across sessions
 * 
 * Usage:
 * const { cart, addItem, removeItem, updateQuantity, switchCompany, isLoading } = useAgentDraftCart(companyId);
 */
const useAgentDraftCart = (selectedCompanyId) => {
  const [cart, setCart] = useState({ items: [], metadata: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  
  // Auto-save debounce timer
  const saveTimeoutRef = useRef(null);
  const previousCompanyIdRef = useRef(null);
  
  // Prevent duplicate calls for tester/bonus items
  // Use a counter that increments atomically to prevent race conditions
  const addItemCounterRef = useRef(0);
  const lastAddItemRef = useRef({ productId: null, timestamp: 0, isTester: false, isBonus: false, callId: 0 });

  // Load cart for the selected company
  const loadCart = useCallback(async (companyId) => {
    if (!companyId) {
      setCart({ items: [], metadata: null });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.get(`/api/field-agent/draft-carts/${companyId}`);
      const draftCart = response.data.draftCart;
      
      if (draftCart && draftCart.cart_data) {
        setCart({
          items: draftCart.cart_data.items || [],
          metadata: draftCart.metadata || null
        });
        setLastSaved(draftCart.last_updated ? new Date(draftCart.last_updated) : null);
      } else {
        // Empty cart for this company
        setCart({ items: [], metadata: null });
        setLastSaved(null);
      }
    } catch (error) {
      console.error('Error loading draft cart:', error);
      toast.error('שגיאה בטעינת עגלת הקניות');
      setCart({ items: [], metadata: null });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save cart to server (debounced)
  const saveCart = useCallback(async (companyId, cartData, immediate = false) => {
    if (!companyId) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const performSave = async () => {
      setIsSaving(true);
      try {
        await axios.post(`/api/field-agent/draft-carts/${companyId}`, {
          cart_data: cartData,
          metadata: cartData.metadata
        });
        setLastSaved(new Date());
      } catch (error) {
        console.error('Error saving draft cart:', error);
        // Silent fail - we don't want to annoy the user with save errors
      } finally {
        setIsSaving(false);
      }
    };

    if (immediate) {
      // Save immediately (e.g., on company switch)
      await performSave();
    } else {
      // Debounced save (2 seconds after last change)
      saveTimeoutRef.current = setTimeout(performSave, 2000);
    }
  }, []);

  // Load cart when company changes
  useEffect(() => {
    const previousCompanyId = previousCompanyIdRef.current;
    
    // Save previous company's cart before switching
    if (previousCompanyId && previousCompanyId !== selectedCompanyId) {
      saveCart(previousCompanyId, cart, true); // Immediate save
    }
    
    // Load new company's cart
    if (selectedCompanyId) {
      loadCart(selectedCompanyId);
    }
    
    previousCompanyIdRef.current = selectedCompanyId;
  }, [selectedCompanyId, loadCart]);

  // Auto-save when cart changes
  useEffect(() => {
    if (selectedCompanyId && cart.items.length >= 0) {
      saveCart(selectedCompanyId, cart, false); // Debounced save
    }
  }, [cart, selectedCompanyId, saveCart]);

  // Add item to cart
  const addItem = useCallback((product, quantity = 1, customPrice = null, options = {}) => {
    // options: { is_tester: boolean, is_bonus: boolean }
    
    // Normalize boolean values for comparison
    const itemIsTester = Boolean(options.is_tester);
    const itemIsBonus = Boolean(options.is_bonus);
    
    // CRITICAL: Prevent duplicate calls for tester/bonus items (React StrictMode causes double calls)
    // Use atomic counter and update ref IMMEDIATELY to prevent race conditions
    if (itemIsTester || itemIsBonus) {
      const now = Date.now();
      const callKey = `${product.id}-${itemIsTester ? 'tester' : ''}-${itemIsBonus ? 'bonus' : ''}`;
      
      // Get current call ID atomically and update ref IMMEDIATELY
      // This ensures the second call will see the updated callId
      const currentCallId = ++addItemCounterRef.current;
      
      // Save previous call info for comparison
      const previousCall = { ...lastAddItemRef.current };
      
      // Update ref IMMEDIATELY before checking (prevents race condition)
      lastAddItemRef.current = {
        productId: product.id,
        timestamp: now,
        isTester: itemIsTester,
        isBonus: itemIsBonus,
        callKey,
        callId: currentCallId
      };
      
      // Now check if this is a duplicate call (using previous call info)
      const timeSinceLastCall = now - previousCall.timestamp;
      const isDuplicate = 
        previousCall.productId === product.id &&
        previousCall.isTester === itemIsTester &&
        previousCall.isBonus === itemIsBonus &&
        timeSinceLastCall < 1000 && // Within 1000ms
        previousCall.callId !== 0; // Not the first call ever
      
      if (isDuplicate) {
        console.log('🚫 BLOCKED duplicate call for tester/bonus:', {
          productId: product.id,
          callKey,
          currentCallId,
          previousCallId: previousCall.callId,
          timeSinceLastCall,
          isTester: itemIsTester,
          isBonus: itemIsBonus,
          previousCallTimestamp: previousCall.timestamp,
          currentTimestamp: now
        });
        // Revert the ref update since this is a duplicate
        lastAddItemRef.current = previousCall;
        return; // Block duplicate call - exit immediately
      }
      
      console.log('✅ ALLOWED call for tester/bonus:', {
        productId: product.id,
        callKey,
        callId: currentCallId,
        timestamp: now,
        isTester: itemIsTester,
        isBonus: itemIsBonus,
        timeSinceLastCall
      });
    }
    
    // CRITICAL: For tester/bonus, IGNORE the quantity parameter completely BEFORE entering setCart
    // This is the FIRST line of defense - don't even let a wrong quantity enter the function
    const originalQuantity = quantity; // Save original before forcing
    if (itemIsTester || itemIsBonus) {
      // FORCE quantity to 1 at the function entry point
      quantity = 1;
      console.log('🔒 FORCED quantity to 1 at function entry for tester/bonus:', {
        productId: product.id,
        originalQuantity: originalQuantity,
        forcedQuantity: quantity
      });
    }
    
    setCart(prevCart => {
      // For tester and bonus items, ALWAYS use quantity = 1, completely ignore any passed quantity
      // This ensures that clicking "Tester" or "Bonus" always adds exactly 1, regardless of any other quantity
      // CRITICAL: Force to 1, don't use quantity parameter at all for tester/bonus
      // Also ignore product.quantity if it exists
      let finalQuantity;
      if (itemIsTester || itemIsBonus) {
        // ABSOLUTELY force to 1, ignore quantity parameter, ignore product.quantity, ignore everything
        // This is the ONLY way to ensure it's always 1
        finalQuantity = 1;
      } else {
        // For regular items, use the passed quantity (but ensure it's a number)
        // Don't use product.quantity even if it exists
        finalQuantity = typeof quantity === 'number' && !isNaN(quantity) ? quantity : 1;
      }
      
      // EXTRA SAFETY: If somehow finalQuantity is not 1 for tester/bonus, force it
      if ((itemIsTester || itemIsBonus) && finalQuantity !== 1) {
        console.warn('⚠️ WARNING: finalQuantity was not 1 for tester/bonus, forcing to 1', {
          finalQuantity,
          passedQuantity: quantity,
          productId: product.id
        });
        finalQuantity = 1;
      }
      
      // Debug log to verify - check if product has quantity that might interfere
      if (itemIsTester || itemIsBonus) {
        console.log('🔍 Adding tester/bonus item:', {
          productId: product.id,
          productName: product.name,
          passedQuantity: quantity,
          finalQuantity: finalQuantity,
          isTester: itemIsTester,
          isBonus: itemIsBonus,
          productHasQuantity: product.quantity,
          productQuantity: product.quantity,
          productKeys: Object.keys(product).filter(k => k.includes('quantity') || k.includes('qty'))
        });
      }
      
      const existingItemIndex = prevCart.items.findIndex(
        item => item.product_id === product.id && 
                Boolean(item.is_tester) === itemIsTester && 
                Boolean(item.is_bonus) === itemIsBonus
      );

      let newItems;
      if (existingItemIndex >= 0) {
        // Update existing item quantity (for both tester and bonus items)
        // Always add exactly 1 for tester/bonus items
        const currentQuantity = prevCart.items[existingItemIndex].quantity;
        newItems = [...prevCart.items];
        // CRITICAL: For tester/bonus, ALWAYS add exactly 1, never use any other quantity
        // This is the second click - must add exactly 1, not the quantity from the counter
        // ABSOLUTE FORCE: Don't even look at finalQuantity for tester/bonus
        if (itemIsTester || itemIsBonus) {
          // HARD-CODED: Always add exactly 1, no exceptions
          const newQuantity = currentQuantity + 1;
          newItems[existingItemIndex].quantity = newQuantity;
          console.log('🔍 Updating existing tester/bonus item - FORCED +1:', {
            productId: product.id,
            currentQuantity,
            adding: 1,
            newQuantity: newQuantity,
            passedQuantity: quantity,
            finalQuantityWas: finalQuantity,
            isTester: itemIsTester,
            isBonus: itemIsBonus
          });
        } else {
          newItems[existingItemIndex].quantity += finalQuantity;
        }
      } else {
        // Add new item - use customPrice (B2B) if provided, fallback to B2B price
        let itemPrice = customPrice || product.price_b2b || product.price_b2c || product.price || product.final_price || 0;
        
        // If tester or bonus, price is always 0
        if (options.is_tester || options.is_bonus) {
          itemPrice = 0;
        }
        
        // Get image from product (can be images array or main_image)
        let productImage = '';
        if (product.images && Array.isArray(product.images) && product.images.length > 0) {
          productImage = product.images[0];
        } else if (product.main_image) {
          productImage = product.main_image;
        } else if (product.image_url) {
          productImage = product.image_url;
        }

        // Build notes
        let notes = options.notes || '';
        if (options.is_tester) {
          notes = notes ? `${notes}\nטסטר - לספק בעלות 0` : 'טסטר - לספק בעלות 0';
        }
        if (options.is_bonus) {
          notes = notes ? `${notes}\nבונוס - מתנה` : 'בונוס - מתנה';
        }

        // For tester/bonus, force quantity to 1 even when creating new item
        // Don't trust finalQuantity - force it to 1 for tester/bonus
        const newItemQuantity = (itemIsTester || itemIsBonus) ? 1 : finalQuantity;
        
        newItems = [
          ...prevCart.items,
          {
            product_id: product.id,
            product_name: product.name || 'מוצר ללא שם',
            sku: product.sku || product.id,
            quantity: newItemQuantity, // Force to 1 for tester/bonus, use finalQuantity for regular items
            price: parseFloat(itemPrice),
            product_image: productImage,
            notes: notes,
            is_tester: options.is_tester || false,
            is_bonus: options.is_bonus || false
          }
        ];
        
        if (itemIsTester || itemIsBonus) {
          console.log('🔍 Creating new tester/bonus item:', {
            productId: product.id,
            newItemQuantity,
            finalQuantityWas: finalQuantity,
            passedQuantity: quantity
          });
        }
      }

      return { ...prevCart, items: newItems };
    });

    const itemType = options.is_bonus ? 'בונוס' : options.is_tester ? 'טסטר' : '';
    toast.success(`${product.name || 'מוצר'} ${itemType ? `(${itemType}) ` : ''}נוסף לעגלה`);
  }, []);

  // Remove item from cart
  const removeItem = useCallback((productId, isTester = false, isBonus = false) => {
    setCart(prevCart => ({
      ...prevCart,
      items: prevCart.items.filter(item =>
        !(item.product_id === productId && 
          Boolean(item.is_tester) === Boolean(isTester) && 
          Boolean(item.is_bonus) === Boolean(isBonus))
      )
    }));
    toast.success('הפריט הוסר מהעגלה');
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((productId, quantity, isTester = false, isBonus = false) => {
    if (quantity <= 0) {
      removeItem(productId, isTester, isBonus);
      return;
    }

    setCart(prevCart => ({
      ...prevCart,
      items: prevCart.items.map(item =>
        item.product_id === productId && 
        Boolean(item.is_tester) === Boolean(isTester) && 
        Boolean(item.is_bonus) === Boolean(isBonus)
          ? { ...item, quantity }
          : item
      )
    }));
  }, [removeItem]);

      // Update item price
      const updatePrice = useCallback((productId, newPrice, extraData = {}) => {
        setCart(prevCart => ({
          ...prevCart,
          items: prevCart.items.map(item => {
            if (item.product_id === productId) {
              // Tester and bonus items must always be 0 price
              const finalPrice = (item.is_tester || item.is_bonus) ? 0 : parseFloat(newPrice);
              return { 
                ...item, 
                price: finalPrice,
                originalPrice: extraData.originalPrice || item.originalPrice,
                discountPercent: extraData.discountPercent || item.discountPercent
              };
            }
            return item;
          })
        }));
      }, []);

  // Update item notes
  const updateNotes = useCallback((productId, notes, isTester = false, isBonus = false) => {
    setCart(prevCart => ({
      ...prevCart,
      items: prevCart.items.map(item =>
        item.product_id === productId && 
        Boolean(item.is_tester) === Boolean(isTester) && 
        Boolean(item.is_bonus) === Boolean(isBonus)
          ? { ...item, notes }
          : item
      )
    }));
  }, []);

  // Clear cart
  const clearCart = useCallback(async () => {
    if (!selectedCompanyId) return;

    try {
      await axios.delete(`/api/field-agent/draft-carts/${selectedCompanyId}`);
      setCart({ items: [], metadata: null });
      setLastSaved(null);
      toast.success('העגלה נוקתה');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast.error('שגיאה בניקוי העגלה');
    }
  }, [selectedCompanyId]);

  // Get total items count
  const getTotalItems = useCallback(() => {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart.items]);

  // Get total price
  const getTotalPrice = useCallback(() => {
    return cart.items.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQty = parseInt(item.quantity) || 0;
      return sum + (itemPrice * itemQty);
    }, 0);
  }, [cart.items]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Final save before unmount
      if (selectedCompanyId && cart.items.length > 0) {
        saveCart(selectedCompanyId, cart, true);
      }
    };
  }, []); // Only run on unmount

  return {
    cart,
    isLoading,
    isSaving,
    lastSaved,
    addItem,
    removeItem,
    updateQuantity,
    updatePrice,
    updateNotes,
    clearCart,
    getTotalItems,
    getTotalPrice,
    reloadCart: () => loadCart(selectedCompanyId)
  };
};

export default useAgentDraftCart;

